<template>
  <div class="keybox-panel">
    <div class="keybox-panel__body">
      <!-- Step 1: Device info -->
      <div class="keybox-panel__section">
        <div class="keybox-panel__section-header">
          <feather-icon icon="KeyIcon" size="18" class="text-primary"/>
          <h6 class="mb-0">{{ msg('Device info') }}</h6>
        </div>
        <b-row>
          <b-col cols="12" md="6">
            <b-form-group :label="msg('IP Address / Hostname')" label-size="sm">
              <b-form-input v-model="keybox.ipAddress" size="sm" placeholder="192.168.0.10:PORT"/>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group :label="msg('Serial Number')" label-size="sm">
              <b-form-input v-model="keybox.serialNumber" size="sm" placeholder="06e79d9d00720f31"/>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group :label="msg('Device Pass')" label-size="sm">
              <b-form-input v-model="keybox.devicePass" size="sm" type="password" placeholder="111111"/>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group :label="msg('Location')" label-size="sm">
              <b-form-input v-model="keybox.location" size="sm" placeholder="e.g. Lobby Entrance"/>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group :label="msg('Status')" label-size="sm">
              <b-form-select v-model="keybox.status" size="sm" :options="statuses"/>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group :label="msg('Installation Status')" label-size="sm">
              <b-form-select v-model="keybox.installationStatus" size="sm" :options="installationStatuses"/>
            </b-form-group>
          </b-col>
        </b-row>
      </div>

      <!-- Step 2: Assignments -->
      <div class="keybox-panel__section">
        <div class="keybox-panel__section-header">
          <feather-icon icon="BuildingIcon" size="18" class="text-primary"/>
          <h6 class="mb-0">{{ msg('Assignments') }}</h6>
        </div>
        <b-row>
          <b-col cols="12" md="6">
            <b-form-group :label="msg('Branch')" label-size="sm">
              <v-select v-model="keybox.buid" :options="allBranches" label="name" :reduce="b=>b.id"
                        :clearable="false" class="select-size-sm"/>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group :label="msg('Building')" label-size="sm">
              <building-select-box v-model="keybox.buildingId"/>
            </b-form-group>
          </b-col>
        </b-row>
      </div>

      <!-- Step 3: Slots -->
      <div class="keybox-panel__section">
        <div class="keybox-panel__section-header">
          <feather-icon icon="LayersIcon" size="18" class="text-primary"/>
          <h6 class="mb-0">{{ msg('Slots') }}</h6>
        </div>
        <b-row v-if="keybox.buildingId">
          <b-col cols="12" md="4">
            <b-form-group :label="msg('Total Slots')" label-size="sm">
              <b-form-select v-model.number="keybox.totalCompartments" size="sm"
                            :options="slotOptions" value-field="value" text-field="text"
                            @change="generateCompartments"/>
            </b-form-group>
          </b-col>
        </b-row>
        <p v-else class="text-muted small mb-0">{{ msg('Select a building first to configure slots') }}</p>

        <div v-if="compartments.length" class="keybox-panel__slots-grid">
          <div v-for="comp in compartments" :key="comp.number" class="keybox-panel__slot-card">
            <div class="keybox-panel__slot-header">
              <span class="font-weight-bolder">{{ msg('Slot') }} #{{ comp.number }}</span>
            </div>
            <real-estate-picker-locker :buildings="[keybox.buildingId]" v-model="comp.realEstateId"/>
          </div>
        </div>
      </div>
    </div>

    <div class="keybox-panel__footer">
      <b-button variant="primary" @click="createKeybox" :disabled="saving">
        <b-spinner v-if="saving" small class="mr-50"/>
        <feather-icon v-else icon="SaveIcon" size="16" class="mr-50"/>
        {{ msg('Save') }}
      </b-button>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import RealEstatePickerLocker from '@/components/components/picker/realEstatesPickerLocker.vue'
import BuildingSelectBox from '@/components/components/picker/buildingSelectBox.vue'

export default {
  name: 'KeyboxPanel',
  components: { BuildingSelectBox, RealEstatePickerLocker },
  data() {
    return {
      saving: false,
      keybox: { totalCompartments: 0 },
      compartments: [],
      statuses: [
        { value: 'online', text: 'Online' },
        { value: 'offline', text: 'Offline' },
        { value: 'error', text: 'Error' }
      ],
      installationStatuses: [
        { value: 'installed', text: 'Installed' },
        { value: 'pending', text: 'Pending' },
        { value: 'failed', text: 'Failed' }
      ],
      slotOptions: [
        { value: 12, text: '12' },
        { value: 24, text: '24' },
        { value: 36, text: '36' }
      ]
    }
  },
  computed: {
    ...mapGetters('translator', ['msg']),
    ...mapGetters('data', ['getAllBranches']),
    allBranches() {
      return Object.values(this.getAllBranches || {})
    }
  },
  methods: {
    ...mapActions('keybox', ['addKeybox']),
    generateCompartments() {
      if (this.keybox.totalCompartments > 50) {
        this.$bvToast.toast(this.msg('Maximum 50 compartments allowed'), {
          title: this.msg('Error'),
          variant: 'danger',
          solid: true
        })
        this.keybox.totalCompartments = 50
      }
      const newCompartments = []
      for (let i = 1; i <= this.keybox.totalCompartments; i++) {
        const existing = this.compartments.find(c => c.number === i)
        newCompartments.push(existing || {
          number: i,
          realEstateId: null,
          isOpen: false,
          accessCode: null
        })
      }
      this.compartments = newCompartments
      this.keybox.compartments = this.compartments
    },
    createKeybox() {
      this.keybox.compartments = this.compartments
      this.saving = true
      this.addKeybox({ keybox: this.keybox })
        .then(id => this.$emit('submitted', { status: true, server: id, id }))
        .catch(err => this.$emit('submitted', { status: false, server: err }))
        .finally(() => { this.saving = false })
    }
  }
}
</script>

<style scoped>
.keybox-panel {
  display: flex;
  flex-direction: column;
  min-height: 200px;
}
.keybox-panel__body { padding: 24px; flex: 1; }
.keybox-panel__footer {
  padding: 16px 24px;
  border-top: 1px solid rgba(0,0,0,.06);
  background: rgba(0,0,0,.02);
}
.dark-layout .keybox-panel__footer {
  border-top-color: rgba(255,255,255,.06);
  background: rgba(0,0,0,.2);
}
.keybox-panel__section {
  margin-bottom: 24px;
  padding-bottom: 24px;
  border-bottom: 1px solid rgba(0,0,0,.06);
}
.keybox-panel__section:last-of-type { border-bottom: none; margin-bottom: 0; padding-bottom: 0; }
.dark-layout .keybox-panel__section { border-bottom-color: rgba(255,255,255,.06); }
.keybox-panel__section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}
.keybox-panel__section-header h6 { font-weight: 600; }
.keybox-panel__slots-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 12px;
  margin-top: 16px;
}
.keybox-panel__slot-card {
  padding: 14px;
  border-radius: 8px;
  background: rgba(115,103,240,.04);
  border: 1px solid rgba(115,103,240,.12);
}
.dark-layout .keybox-panel__slot-card {
  background: rgba(115,103,240,.08);
  border-color: rgba(115,103,240,.2);
}
.keybox-panel__slot-header { margin-bottom: 10px; font-size: .9rem; }
</style>
